import { BOOK_PLANS } from '@kitup/book-schema';
import type { BookDto } from '../api/books.js';
import type { Dictionary } from '../i18n/registry.js';

/**
 * `optional` — an optional step with nothing in it, which a book is complete without;
 * `unavailable` — a step the book's plan does not include. Both are settled, but neither is
 * "done", and the sidebar must not paint them the same green as a finished step.
 */
export type StepState = 'empty' | 'working' | 'ready' | 'error' | 'optional' | 'unavailable';

export interface BuilderStep {
  readonly id: string;
  readonly label: string;
  readonly state: StepState;
}

/** Статусы вычисляются только из данных книги, а не из посещённых экранов. */
export function deriveBuilderSteps(
  book: BookDto,
  labels: Dictionary['builder']['steps'],
  allCharactersApproved = false,
  hasReadyThing = false,
  allLocationsReady = false,
): readonly BuilderStep[] {
  const plan = BOOK_PLANS[book.plan];
  const scenesReady = book.sceneSummary.total >= book.sceneCount;
  const illustrationsReady =
    book.sceneSummary.generated >= book.sceneCount && book.sceneSummary.stale === 0;
  return [
    {
      id: 'main',
      label: labels.main,
      state: book.title && book.styleId ? 'ready' : 'empty',
    },
    {
      id: 'characters',
      label: labels.characters,
      state:
        book.composition.characters === 0 ? 'empty' : allCharactersApproved ? 'ready' : 'working',
    },
    {
      id: 'things',
      label: labels.things,
      state: hasReadyThing ? 'ready' : book.composition.things > 0 ? 'working' : 'optional',
    },
    {
      id: 'locations',
      label: labels.locations,
      // Every place, as with characters: illustrations leave out a place with no chosen drawing
      // in the book's style, so each undrawn place is still work to do.
      state:
        book.composition.locations === 0 ? 'optional' : allLocationsReady ? 'ready' : 'working',
    },
    {
      id: 'story',
      label: labels.story,
      state: scenesReady ? 'ready' : book.sceneSummary.total > 0 ? 'working' : 'empty',
    },
    {
      id: 'illustrations',
      label: labels.illustrations,
      state:
        book.sceneSummary.stale > 0
          ? 'error'
          : illustrationsReady
            ? 'ready'
            : book.sceneSummary.generated > 0
              ? 'working'
              : 'empty',
    },
    {
      id: 'cover',
      label: labels.cover,
      state:
        book.coverAssetId && book.backCoverAssetId
          ? 'ready'
          : book.coverAssetId || book.backCoverAssetId
            ? 'working'
            : 'empty',
    },
    {
      id: 'video',
      label: labels.video,
      state: !plan.video.enabled
        ? 'unavailable'
        : book.sceneSummary.videos >= book.sceneCount
          ? 'ready'
          : book.sceneSummary.videos > 0
            ? 'working'
            : 'empty',
    },
    {
      id: 'narration',
      label: labels.narration,
      // Whether the reader plays narration is a reading setting, not progress: the step is
      // in progress once a spread is narrated, not the moment a plan with narration is picked.
      state: !plan.narration
        ? 'unavailable'
        : book.sceneCount > 0 && book.sceneSummary.narrated >= book.sceneCount
          ? 'ready'
          : book.sceneSummary.narrated > 0
            ? 'working'
            : 'empty',
    },
    {
      id: 'export',
      label: labels.export,
      state: book.publishedAt || book.hasExport ? 'ready' : 'empty',
    },
  ];
}
